'use client';

import { useEffect, useState } from 'react';
import { Zap, Star } from 'lucide-react';
import { getLevelInfo, getRankTitle } from '@/lib/gamification';

interface XpToastProps {
  amount: number;
  reason: string;
  newXp?: number;
  newLevel?: number;
  prevLevel?: number;
  onDismiss: () => void;
}

export function XpToast({ amount, reason, newXp, newLevel, prevLevel, onDismiss }: XpToastProps) {
  const [visible, setVisible] = useState(false);
  const leveledUp = newLevel && prevLevel && newLevel > prevLevel;

  useEffect(() => {
    // Animate in
    const t1 = setTimeout(() => setVisible(true), 50);
    // Auto dismiss after 3.5s
    const t2 = setTimeout(() => {
      setVisible(false);
      setTimeout(onDismiss, 300);
    }, 3500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDismiss]);

  return (
    <div
      className="fixed bottom-24 right-4 z-50 transition-all duration-300 pointer-events-none"
      style={{
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.9)',
        opacity: visible ? 1 : 0,
      }}
    >
      {leveledUp ? (
        <div
          className="rounded-xl px-4 py-3 flex items-center gap-3 shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.2), rgba(139,92,246,0.2))',
            border: '1px solid rgba(245,158,11,0.5)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 0 20px rgba(245,158,11,0.3)',
          }}
        >
          <div className="text-2xl">🎉</div>
          <div>
            <p className="text-sm font-bold" style={{ color: '#f59e0b' }}>Level Up!</p>
            <p className="text-xs" style={{ color: '#e8ecf0' }}>
              You reached <span className="font-bold">Level {newLevel}</span> — {getRankTitle(newLevel!)}
            </p>
          </div>
        </div>
      ) : (
        <div
          className="rounded-xl px-4 py-2.5 flex items-center gap-2.5 shadow-xl"
          style={{
            background: 'rgba(13,15,20,0.95)',
            border: '1px solid rgba(99, 102, 241,0.35)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <div
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'rgba(99, 102, 241,0.2)' }}
          >
            <Zap size={14} style={{ color: '#60a5fa' }} />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: '#60a5fa' }}>+{amount} XP</p>
            <p className="text-xs" style={{ color: '#6b7280' }}>{reason}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Level Badge Component ──────────────────────────────────────────────────
interface LevelBadgeProps {
  level: number;
  size?: 'sm' | 'md' | 'lg';
  showTitle?: boolean;
}

export function LevelBadge({ level, size = 'sm', showTitle = false }: LevelBadgeProps) {
  const info = getLevelInfo(0); // just for rank color
  const rankTitle = getRankTitle(level);

  const sizeMap = {
    sm: { px: '6px', py: '2px', fontSize: '10px', iconSize: 9 },
    md: { px: '8px', py: '3px', fontSize: '11px', iconSize: 11 },
    lg: { px: '10px', py: '4px', fontSize: '12px', iconSize: 13 },
  };
  const s = sizeMap[size];

  // Color by level range
  let color = '#9ca3af';
  let bg = 'rgba(107,114,128,0.12)';
  let border = 'rgba(107,114,128,0.3)';
  if (level >= 50) { color = '#f59e0b'; bg = 'rgba(245,158,11,0.15)'; border = 'rgba(245,158,11,0.4)'; }
  else if (level >= 31) { color = '#a78bfa'; bg = 'rgba(167,139,250,0.15)'; border = 'rgba(167,139,250,0.4)'; }
  else if (level >= 16) { color = '#60a5fa'; bg = 'rgba(99, 102, 241,0.15)'; border = 'rgba(99, 102, 241,0.4)'; }
  else if (level >= 6) { color = '#4ade80'; bg = 'rgba(34,197,94,0.12)'; border = 'rgba(34,197,94,0.35)'; }

  return (
    <span
      className="inline-flex items-center gap-1 rounded font-bold shrink-0"
      style={{ background: bg, color, border: `1px solid ${border}`, padding: `${s.py} ${s.px}`, fontSize: s.fontSize }}
      title={`Level ${level} — ${rankTitle}`}
    >
      <Star size={s.iconSize} fill={color} />
      {showTitle ? `Lv.${level} ${rankTitle}` : `Lv.${level}`}
    </span>
  );
}

// ── XP Progress Bar Component ──────────────────────────────────────────────
interface XpProgressBarProps {
  xp: number;
  level: number;
  compact?: boolean;
}

export function XpProgressBar({ xp, level, compact = false }: XpProgressBarProps) {
  const info = getLevelInfo(xp);

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <LevelBadge level={level} size="sm" />
        <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${info.progress}%`,
              background: `linear-gradient(90deg, ${info.rankColor}, ${info.rankColor}cc)`,
            }}
          />
        </div>
        <span className="text-xs font-mono shrink-0" style={{ color: '#6b7280' }}>{xp} XP</span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LevelBadge level={level} size="md" showTitle />
        </div>
        <span className="text-xs font-mono" style={{ color: '#6b7280' }}>
          {xp} / {info.xpForNextLevel} XP
        </span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${info.progress}%`,
            background: `linear-gradient(90deg, ${info.rankColor}cc, ${info.rankColor})`,
            boxShadow: `0 0 8px ${info.rankColor}66`,
          }}
        />
      </div>
      <div className="flex items-center justify-between text-xs" style={{ color: '#4b5563' }}>
        <span>Level {level}</span>
        <span>{info.xpForNextLevel - xp} XP to Level {level + 1}</span>
      </div>
    </div>
  );
}
